import { NAVIGATION_ROUTES } from '../../constant';
import type {
  IStatusScreen,
  IStatusState,
  IWithdrawCta,
  WithdrawStatusKey,
} from '../../interfaces/Withdrawal';

/**
 * The status screen carries a block per withdrawal type (`WITHDRAW`, `CTD`),
 * each with a current `status` and one payload per possible state. Phase 1
 * renders the `WITHDRAW` block only.
 */

const STATUS_PREFIX = 'status_';
const STATUS_KEYS: WithdrawStatusKey[] = [
  'success',
  'fail',
  'progress',
  'pending',
];

/**
 * Normalises the wire value to a payload key.
 *
 * The block reports `status: "status_success"` while its payload sits under
 * `success` — indexing with the raw value silently yields `undefined`, which
 * renders an empty screen.
 */
export function getWithdrawStatusKey(
  statusScreen?: IStatusScreen,
): WithdrawStatusKey | undefined {
  const raw = statusScreen?.WITHDRAW?.status;
  if (!raw) return undefined;

  const normalised = String(raw).replace(STATUS_PREFIX, '').toLowerCase();
  return STATUS_KEYS.find((key) => key === normalised);
}

export function getWithdrawStatusState(
  statusScreen?: IStatusScreen,
): IStatusState | undefined {
  const block = statusScreen?.WITHDRAW;
  const key = getWithdrawStatusKey(statusScreen);
  if (!block || !key) return undefined;

  return block[key];
}

/** Terminal states need no further socket updates. */
export function isTerminalStatus(status?: WithdrawStatusKey): boolean {
  return status === 'success' || status === 'fail';
}

/** Live payloads label CTAs with `displayText`; `text` is a fallback. */
export function getCtaLabel(cta?: IWithdrawCta): string {
  return cta?.displayText || cta?.text || '';
}

/**
 * Where the status screen's CTA sends the user.
 *
 * The server names a destination rather than a route — `{type: "IN_APP",
 * value: "WALLET"}` on progress and pending, `"DASHBOARD"` on success. Android
 * resolves the same values through `GenericCTAHandler.ScreenNames`
 * (`core/utils/GenericCTAHandler.java:903-930`; `WALLET` → `:371-374`,
 * `DASHBOARD` → `:440-441`).
 *
 * The wallet is the default: it is this flow's parent, and where a user who has
 * just moved money expects to land.
 */
const CTA_ROUTES: Record<string, string> = {
  WALLET: NAVIGATION_ROUTES.WALLET,
  DASHBOARD: NAVIGATION_ROUTES.HOME,
  WITHDRAWAL: NAVIGATION_ROUTES.WITHDRAW,
};

export function getCtaRoute(cta?: IWithdrawCta): string {
  // Only in-app destinations are routes; anything else falls through.
  if (cta?.type && cta.type !== 'IN_APP') return NAVIGATION_ROUTES.WALLET;
  return CTA_ROUTES[cta?.value ?? ''] ?? NAVIGATION_ROUTES.WALLET;
}

/**
 * Seconds to sit on `progress` before the client itself calls it `pending`.
 * Android: `PROGRESS_TO_PENDING_DELAY` (`payments/utils/PaymentConstants.kt:7`).
 * The server can override it per state via `processingDelay`.
 */
export const PROGRESS_TO_PENDING_DELAY = 10;

/**
 * Milliseconds the primary CTA stays withheld while `progress` runs, so the user
 * cannot leave before the animation has said anything.
 * Android: `WITHDRAW_STATUS_CTA_ENABLE_DELAY` (`payments/utils/PaymentConstants.kt:6`).
 */
export const WITHDRAW_STATUS_CTA_ENABLE_DELAY = 5000;

/**
 * Returns the screen with its WITHDRAW block forced to `pending`.
 *
 * Android does the same rewrite in `WithdrawStatusFragment.setCountdown` — it
 * copies the status screen with `status = PENDING.state` and re-emits it rather
 * than asking the server, because a payout that is still `progress` after the
 * processing window is, by definition, pending.
 *
 * Written unprefixed: `getWithdrawStatusKey` strips `status_` when present, so
 * `'pending'` normalises to the same key as the wire's `'status_pending'`.
 */
export function withPendingStatus(statusScreen: IStatusScreen): IStatusScreen {
  return {
    ...statusScreen,
    WITHDRAW: { ...statusScreen.WITHDRAW, status: 'pending' },
  };
}

/** Transaction detail rows are a receipt — Android shows them only once paid. */
export function shouldShowTransactionDetails(
  status?: WithdrawStatusKey,
): boolean {
  return status === 'success';
}
